import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import HelpGenieLogo from "@/components/ui/HelpGenieLogo";

const MAGENTA_BEAM = "#FF4DB8";
const SPARK_BLUE = "#4E9CFF";

export default function UpdatePasswordPage() {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [hasValidSession, setHasValidSession] = useState<boolean | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data, error: sessionError } = await supabase.auth.getSession();
        if (sessionError || !data?.session) {
          setError("Your reset link is invalid or expired.");
          setHasValidSession(false);
        } else {
          setHasValidSession(true);
        }
      } catch {
        setError("Failed to verify your session.");
        setHasValidSession(false);
      }
    };
    checkSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters long.");
      return;
    }
    if (!hasValidSession) {
      setError("No valid session found. Please try the reset link again.");
      return;
    }

    setLoading(true);
    const { error: updateError } = await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(updateError.message || "Failed to update password. Please try again.");
    } else {
      setSuccess(true);
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }
    setLoading(false);
  };

  const renderContent = () => {
    // Loading state
    if (hasValidSession === null) {
      return (
        <div className="text-center">
          <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center">
            <Lock className="h-6 w-6 text-gray-400 animate-pulse" />
          </div>
          <p className="text-gray-500">Verifying your reset link...</p>
        </div>
      );
    }

    // Invalid/expired session
    if (!hasValidSession) {
      return (
        <div className="text-center">
          <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
            <Lock className="h-6 w-6 text-red-400" />
          </div>
          <h2
            className="text-2xl md:text-3xl font-bold mb-3"
            style={{
              background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Link Expired
          </h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Link to="/forgot-password">
            <Button className="bg-genie-primary hover:bg-genie-primary/90 text-white">
              Request a New Link
            </Button>
          </Link>
        </div>
      );
    }

    // Success state
    if (success) {
      return (
        <div className="text-center">
          <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
            <CheckCircle className="h-6 w-6 text-green-500" />
          </div>
          <h2
            className="text-2xl md:text-3xl font-bold mb-3"
            style={{
              background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Password Updated!
          </h2>
          <p className="text-gray-500 mb-6">
            Your password has been set successfully. You can now sign in with your new password.
          </p>
          <Link to="/signin">
            <Button className="bg-genie-primary hover:bg-genie-primary/90 text-white">
              Sign In
            </Button>
          </Link>
        </div>
      );
    }

    // Form state
    return (
      <>
        <div className="text-center mb-6">
          <div className="mb-4 mx-auto w-12 h-12 rounded-full flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${MAGENTA_BEAM}15, ${SPARK_BLUE}15)` }}>
            <Lock className="h-6 w-6 text-genie-primary" />
          </div>
          <h2
            className="text-2xl md:text-3xl font-bold mb-2"
            style={{
              background: `linear-gradient(135deg, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Set Your Password
          </h2>
          <p className="text-gray-500 text-sm">
            Choose a secure password for your account.
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-sm font-medium text-gray-700">New Password</Label>
            <Input
              id="new-password"
              type="password"
              required
              placeholder="Enter your new password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-sm font-medium text-gray-700">Confirm Password</Label>
            <Input
              id="confirm-password"
              type="password"
              required
              placeholder="Confirm your new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="h-11"
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 text-center p-3 bg-red-50 rounded-lg border border-red-100">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-genie-primary hover:bg-genie-primary/90 text-white font-medium rounded-lg"
          >
            {loading ? "Updating..." : "Update Password"}
          </Button>
        </form>

        <div className="text-center mt-4">
          <Link to="/forgot-password" className="text-sm text-genie-primary hover:text-genie-primary/80 transition-colors">
            Request a new reset link
          </Link>
        </div>
      </>
    );
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-12"
      style={{
        background: `linear-gradient(135deg, ${MAGENTA_BEAM}08, white 50%, ${SPARK_BLUE}08)`,
      }}
    >
      <div className="flex justify-center mb-8">
        <HelpGenieLogo size={56} />
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-md p-8 md:p-10">
        {renderContent()}
      </div>

      <p className="mt-6 text-xs text-gray-400">
        &copy; {new Date().getFullYear()} Help Genie. All rights reserved.
      </p>
    </div>
  );
}
